import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';

// ─────────────────────────────────────────
// Custom Recharts Tooltip
// ─────────────────────────────────────────
const GoldTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'rgba(18,5,5,0.95)',
      border: '1px solid rgba(212,175,55,0.3)',
      borderRadius: 12,
      padding: '10px 16px',
      boxShadow: '0 8px 32px rgba(0,0,0,0.4)'
    }}>
      <p style={{ color: '#E8B84B', fontSize: 11, fontWeight: 700, marginBottom: 4 }}>{label}</p>
      <p style={{ color: '#fff', fontSize: 15, fontWeight: 800 }}>
        ₹{payload[0]?.value?.toLocaleString('en-IN') || 0}
      </p>
    </div>
  );
};

// ─────────────────────────────────────────
// Activity item type → icon + color
// ─────────────────────────────────────────
const activityMeta = (type) => {
  switch (type) {
    case 'donation': return { icon: 'volunteer_activism', color: '#E8B84B', bg: 'rgba(212,175,55,0.12)' };
    case 'order':    return { icon: 'receipt_long',       color: '#22c55e', bg: 'rgba(34,197,94,0.12)' };
    case 'activity': return { icon: 'auto_awesome',       color: '#a78bfa', bg: 'rgba(167,139,250,0.12)' };
    default:         return { icon: 'notifications',      color: '#94a3b8', bg: 'rgba(148,163,184,0.1)' };
  }
};

// ─────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────
const CentreDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const feedRef = useRef(null);

  const [centre, setCentre]         = useState(null);
  const [revenue, setRevenue]       = useState(null);
  const [orders, setOrders]         = useState([]);
  const [donations, setDonations]   = useState([]);
  const [offerings, setOfferings]   = useState([]);   // SpiritualActivity (Pushpanjali, Deepam, etc.)
  const [shopProducts, setShopProducts] = useState([]); // Centre Shop products
  const [loading, setLoading]       = useState(true);
  const [chartToggle, setChartToggle] = useState('donations');
  const [goLiveLoading, setGoLiveLoading] = useState(false); // eslint-disable-line no-unused-vars

  // LIVE: real-time polling every 15s keeps every number a fresh aggregation
  useEffect(() => { fetchAll(); const id = setInterval(fetchAll, 15000); return () => clearInterval(id); }, []);

  const fetchAll = async () => {
    try {
      const [centreRes, revenueRes, ordersRes, donationsRes, offeringsRes, shopRes] = await Promise.allSettled([
        api.get('/admin/centres'),
        api.get('/admin/revenue'),
        api.get('/admin/orders'),
        api.get('/donations'),
        api.get('/spiritual-activities/for-centre'),
        api.get('/centre-shop'),
      ]);

      if (centreRes.status === 'fulfilled') {
        const centres = centreRes.value.data?.data || [];
        setCentre(centres[0] || null);
      }
      if (revenueRes.status === 'fulfilled') {
        setRevenue(revenueRes.value.data?.data || null);
      }
      if (ordersRes.status === 'fulfilled') {
        setOrders(ordersRes.value.data?.data || []);
      }
      if (donationsRes.status === 'fulfilled') {
        setDonations(donationsRes.value.data?.data || []);
      }
      if (offeringsRes.status === 'fulfilled') {
        setOfferings(offeringsRes.value.data?.data || []);
      }
      if (shopRes.status === 'fulfilled') {
        setShopProducts(shopRes.value.data?.data || []);
      }
    } catch (err) {
      console.error('CentreDashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  };


  // ── Derived metrics ──────────────────────────────────────
  const trustee = user?.name || 'Trustee';
  const centreName = centre?.name || 'Your Centre';

  const now = new Date();
  const dayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const last24h = new Date(now.getTime() - 24 * 60 * 60 * 1000);

  // Orders from the Order collection (physical deliveries + marketplace)
  const todayOrders    = orders.filter(o => new Date(o.createdAt) >= dayStart && (o.status || '').toLowerCase() !== 'cancelled');
  const dailyOfferings = todayOrders.reduce((sum, o) => sum + (o.totalAmount || 0), 0);
  const pendingOrders  = orders.filter(o => !['Delivered', 'Cancelled'].includes(o.status)).length;

  // Donations from the Donation collection (custom contributions + shop donations)
  const recentDonations  = donations.filter(d => new Date(d.createdAt) >= last24h);
  const recentOfferings  = offerings.filter(o => new Date(o.createdAt) >= last24h);
  const sacredContributions = (revenue?.breakdown?.donations || 0) + (revenue?.breakdown?.offerings || 0);

  // REAL DATA: viewerCount is the Centre document's live field — no synthetic viewers.
  const liveViewers = centre?.isLive ? (centre?.viewerCount || 0) : 0;

  // ── Build activity feed (all 3 streams) ─────────────────────
  const orderFeedItems = orders.slice(0, 4).map(o => ({
    id:       o._id,
    type:     'order',
    user:     o.userId?.name || 'A devotee',
    itemName: o.productName || o.productId?.name || 'Store Item',
    quantity: o.quantity || 1,
    amount:   o.totalAmount || 0,
    status:   o.status || 'Pending',
    time:     o.createdAt
  }));

  const donationFeedItems = donations.slice(0, 3).map(d => ({
    id:       d._id,
    type:     'donation',
    user:     d.donorName || 'A devotee',
    itemName: d.offeringName || 'Sacred Contribution',
    amount:   d.amount || 0,
    status:   d.status || 'Completed',
    time:     d.createdAt
  }));

  const offeringFeedItems = offerings.slice(0, 3).map(o => ({
    id:       o._id,
    type:     'activity',
    user:     o.customerName || o.userId?.name || 'A devotee',
    itemName: o.serviceName || 'Centre Ritual',
    amount:   o.price || 0,
    status:   o.status || 'Booked',
    time:     o.createdAt
  }));

  const feed = [...orderFeedItems, ...donationFeedItems, ...offeringFeedItems]
    .sort((a, b) => new Date(b.time) - new Date(a.time))
    .slice(0, 9);

  // ── Chart data from revenue API ──────────────────────────
  const activeChartData = (revenue?.chartData || []).map(d => ({
    month: d.month,
    // backend now sends 'donations' and 'orders' (not 'sales')
    value: chartToggle === 'donations' ? (d.donations || 0) : (d.orders || 0)
  }));

  // ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'transparent' }}>
        <div className="text-center">
          <div className="w-16 h-16 rounded-full border-4 border-[#E8B84B]/20 border-t-[#E8B84B] animate-spin mx-auto mb-6" />
          <p className="text-[#E8B84B] font-serif text-lg tracking-widest">LOADING SANCTUM...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-16" style={{ background: 'linear-gradient(160deg, rgba(18,11,46,.5) 0%, rgba(12,7,36,.28) 40%, rgba(6,3,17,0) 100%)' }}>

      {/* ══════════════════════════════════════════
          A. HERO GREETING
      ══════════════════════════════════════════ */}
      <div className="relative overflow-hidden px-8 pt-10 pb-8">
        {/* Background mandala glow */}
        <div className="absolute inset-0 pointer-events-none" style={{
          background: 'radial-gradient(ellipse 60% 80% at 80% 50%, rgba(212,175,55,0.06) 0%, transparent 70%)'
        }} />
        {/* Decorative Om symbol */}
        <div className="absolute right-8 top-4 text-[140px] font-serif select-none pointer-events-none"
          style={{ color: 'rgba(212,175,55,0.05)', lineHeight: 1 }}>ॐ</div>

        <div className="relative z-10 flex items-start justify-between flex-wrap gap-6">
          <div>
            {/* Greeting */}
            <p className="text-[#E8B84B]/70 text-xs font-bold uppercase tracking-[0.35em] mb-3">
              {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}
            </p>
            <h1 className="font-serif text-3xl font-bold leading-tight mb-2" style={{ color: '#FDF6E3' }}>
              🙏 Jai Shri Ram, {trustee}!
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <span className="material-symbols-outlined text-[16px]" style={{ color: '#E8B84B' }}>account_balance</span>
              <p style={{ color: 'rgba(212,175,55,0.8)', fontSize: 13, fontWeight: 600 }}>
                Managing: <span style={{ color: '#E8B84B' }}>{centreName}</span>
              </p>
            </div>
            {centre?.isLive && (
              <div className="flex items-center gap-2 mt-3 px-3 py-1.5 rounded-full w-fit"
                style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)' }}>
                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                <span className="text-red-400 text-[10px] font-black uppercase tracking-widest">Live Darshan Active</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="px-8 space-y-8">

        {/* ══════════════════════════════════════════
            B. DIVINE METRIC CARDS
        ══════════════════════════════════════════ */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Card 1 — Live Viewers */}
          <MetricCard
            icon="groups"
            label="Devotee Footfall"
            value={liveViewers}
            unit="live viewers"
            accentColor="#E8B84B"
            trend={centre?.isLive ? 'LIVE' : 'OFFLINE'}
            trendColor={centre?.isLive ? '#22c55e' : '#64748b'}
          />
          {/* Card 2 — Today's Order Revenue */}
          <MetricCard
            icon="shopping_bag"
            label="Today's Orders"
            value={`₹${dailyOfferings.toLocaleString('en-IN')}`}
            unit="marketplace sales today"
            accentColor="#22c55e"
            trend={`${todayOrders.length} order${todayOrders.length !== 1 ? 's' : ''}`}
          />
          {/* Card 3 — Sacred Contributions */}
          <MetricCard
            icon="volunteer_activism"
            label="Sacred Contributions"
            value={`₹${sacredContributions.toLocaleString('en-IN')}`}
            unit="donations + offerings total"
            accentColor="#a78bfa"
            trend={`${recentDonations.length + recentOfferings.length} in last 24h`}
          />
          {/* Card 4 — Pending Orders */}
          <MetricCard
            icon="pending_actions"
            label="Pending Orders"
            value={pendingOrders}
            unit="awaiting fulfilment"
            accentColor="#f87171"
            trend={pendingOrders > 0 ? 'Needs attention' : 'All clear'}
            trendColor={pendingOrders > 0 ? '#f87171' : '#22c55e'}
          />
        </div>

        {/* ══════════════════════════════════════════
            C + D. REVENUE CHART + ACTIVITY FEED
        ══════════════════════════════════════════ */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">

          {/* Revenue Area Chart */}
          <div className="lg:col-span-3 rounded-3xl p-6 relative overflow-hidden"
            style={{
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(212,175,55,0.15)',
              backdropFilter: 'blur(12px)'
            }}>
            {/* Header */}
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
              <div>
                <h3 className="font-serif font-bold text-lg" style={{ color: '#FDF6E3' }}>
                  Spiritual Revenue Trends
                </h3>
                <p className="text-xs mt-0.5" style={{ color: 'rgba(212,175,55,0.6)' }}>
                  Monthly overview
                </p>
              </div>
              {/* Toggle */}
              <div className="flex rounded-xl overflow-hidden" style={{ border: '1px solid rgba(212,175,55,0.2)' }}>
                {[['donations', 'Donations'], ['orders', 'Orders']].map(([key, label]) => (
                  <button
                    key={key}
                    onClick={() => setChartToggle(key)}
                    className="px-4 py-1.5 text-[10px] font-black uppercase tracking-widest transition-all"
                    style={{
                      background: chartToggle === key ? 'rgba(212,175,55,0.2)' : 'transparent',
                      color: chartToggle === key ? '#E8B84B' : 'rgba(212,175,55,0.4)',
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Total Badge */}
            <div className="mb-6">
              <span className="text-3xl font-bold" style={{ color: '#E8B84B' }}>
                ₹{(revenue?.total || 0).toLocaleString('en-IN')}
              </span>
              <span className="text-xs ml-3" style={{ color: 'rgba(212,175,55,0.5)' }}>total lifetime revenue</span>
            </div>

            {/* Chart */}
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={activeChartData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="goldGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#E8B84B" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#E8B84B" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(212,175,55,0.06)" vertical={false} />
                <XAxis
                  dataKey="month"
                  tick={{ fill: 'rgba(212,175,55,0.4)', fontSize: 10, fontWeight: 700 }}
                  axisLine={false} tickLine={false}
                />
                <YAxis
                  tick={{ fill: 'rgba(212,175,55,0.3)', fontSize: 9 }}
                  axisLine={false} tickLine={false}
                  tickFormatter={v => {
                    if (v === 0) return '';
                    if (v >= 100000) return `₹${(v/100000).toFixed(1)}L`;
                    if (v >= 1000) return `₹${(v/1000).toFixed(0)}k`;
                    return `₹${v}`;
                  }}
                />
                <Tooltip content={<GoldTooltip />} cursor={{ stroke: 'rgba(212,175,55,0.2)', strokeWidth: 1 }} />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="#E8B84B"
                  strokeWidth={2.5}
                  fill="url(#goldGradient)"
                  dot={{ r: 3, fill: '#E8B84B', strokeWidth: 0 }}
                  activeDot={{ r: 5, fill: '#E8B84B', stroke: 'rgba(212,175,55,0.4)', strokeWidth: 6 }}
                />
              </AreaChart>
            </ResponsiveContainer>

            {/* Breakdown pills */}
            {revenue?.breakdown && (
              <div className="flex gap-4 mt-5 flex-wrap">
                {[
                  { label: 'Donations',  val: revenue.breakdown.donations || 0,  color: '#E8B84B' },
                  { label: 'Offerings',  val: revenue.breakdown.offerings || 0,  color: '#a78bfa' },
                  { label: 'Orders',     val: revenue.breakdown.orders    || 0,  color: '#22c55e' },
                ].map(b => (
                  <div key={b.label} className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ background: b.color }} />
                    <span className="text-[10px] font-bold" style={{ color: 'rgba(255,255,255,0.4)' }}>
                      {b.label}: <span style={{ color: b.color }}>₹{(b.val).toLocaleString('en-IN')}</span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── Live Activity Feed ─────────────────── */}
          <div className="lg:col-span-2 rounded-3xl p-6 relative overflow-hidden flex flex-col"
            style={{
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(212,175,55,0.15)',
              backdropFilter: 'blur(12px)'
            }}>
            <div className="flex items-center gap-2 mb-5">
              <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              <h3 className="font-serif font-bold text-sm" style={{ color: '#FDF6E3' }}>
                Live Activity
              </h3>
            </div>

            {feed.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center py-8">
                <span className="text-5xl mb-3" style={{ opacity: 0.2 }}>🪔</span>
                <p className="text-xs" style={{ color: 'rgba(212,175,55,0.4)' }}>Awaiting devotees...</p>
              </div>
            ) : (
              <div ref={feedRef} className="flex-1 space-y-3 overflow-y-auto pr-1" style={{ maxHeight: 320 }}>
                {feed.map((item, i) => {
                  const meta = activityMeta(item.type);
                  const timeAgo = getTimeAgo(item.time);
                  return (
                    <div
                      key={item.id || i}
                      className="flex items-start gap-3 p-3 rounded-2xl transition-all hover:opacity-90"
                      style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}
                    >
                      <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5"
                        style={{ background: meta.bg }}>
                        <span className="material-symbols-outlined text-[16px]" style={{ color: meta.color }}>
                          {meta.icon}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        {/* Title: Item Name with optional quantity */}
                        <p className="text-sm font-bold truncate" style={{ color: '#FDF6E3' }}>
                          {item.quantity && item.quantity > 1 && <span className="text-[#E8B84B] mr-1">{item.quantity}x</span>}
                          {item.itemName}
                        </p>
                        {/* Subtitle: User/Devotee Name */}
                        <p className="text-[10px] mt-0.5 truncate" style={{ color: 'rgba(255,255,255,0.5)' }}>
                          By <span className="font-medium text-white">{item.user}</span>
                        </p>
                        
                        <div className="flex items-center justify-between mt-2">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-black" style={{ color: meta.color }}>
                              ₹{item.amount.toLocaleString('en-IN')}
                            </span>
                            <span className="px-2 py-0.5 rounded text-[9px] font-bold" 
                                  style={{ background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)' }}>
                              {item.status}
                            </span>
                          </div>
                          <span className="text-[9px]" style={{ color: 'rgba(255,255,255,0.3)' }}>
                            {timeAgo}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <button
              onClick={() => navigate('/orders')}
              className="mt-4 w-full py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all hover:opacity-80"
              style={{
                background: 'rgba(212,175,55,0.1)',
                border: '1px solid rgba(212,175,55,0.2)',
                color: '#E8B84B'
              }}
            >
              View All Activity →
            </button>
          </div>
        </div>

        {/* ── Quick stats bar ───────────────────────── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
          { label: 'Total Revenue',    val: `₹${(revenue?.total || 0).toLocaleString('en-IN')}`, icon: 'savings',          color: '#E8B84B' },
            { label: 'Total Orders',     val: orders.length,                                        icon: 'receipt_long',     color: '#22c55e' },
            { label: 'Donations',        val: donations.length,                                     icon: 'volunteer_activism', color: '#a78bfa' },
            { label: 'Centre Shop',      val: shopProducts.length,                                  icon: 'storefront',       color: '#f59e0b' },
          ].map(s => (
            <div key={s.label} className="rounded-2xl px-5 py-4 flex items-center gap-4"
              style={{
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.07)'
              }}>
              <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: `${s.color}18` }}>
                <span className="material-symbols-outlined text-[18px]" style={{ color: s.color }}>{s.icon}</span>
              </div>
              <div>
                <div className="font-bold text-base" style={{ color: '#FDF6E3' }}>{s.val}</div>
                <div className="text-[9px] font-bold uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.3)' }}>{s.label}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────
// Metric Card Sub-component
// ─────────────────────────────────────────
const MetricCard = ({ icon, label, value, unit, accentColor, trend, trendColor }) => (
  <div className="rounded-2xl p-5 relative overflow-hidden group hover:scale-[1.02] transition-transform cursor-default"
    style={{
      background: 'rgba(255,255,255,0.03)',
      border: `1px solid ${accentColor}22`,
      backdropFilter: 'blur(8px)'
    }}>
    {/* Glow blob */}
    <div className="absolute -bottom-4 -right-4 w-20 h-20 rounded-full opacity-10 transition-opacity group-hover:opacity-20"
      style={{ background: accentColor, filter: 'blur(20px)' }} />

    <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
      style={{ background: `${accentColor}18` }}>
      <span className="material-symbols-outlined text-[20px]" style={{ color: accentColor }}>{icon}</span>
    </div>

    <div className="font-bold text-2xl leading-none mb-1.5" style={{ color: '#FDF6E3' }}>
      {value}
    </div>
    <div className="text-[9px] font-black uppercase tracking-widest mb-2" style={{ color: `${accentColor}99` }}>
      {label}
    </div>
    <div className="text-[10px]" style={{ color: trendColor || 'rgba(255,255,255,0.3)' }}>
      {trend}
    </div>
  </div>
);

// ─────────────────────────────────────────
// Time ago helper
// ─────────────────────────────────────────
function getTimeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default CentreDashboard;
